import React from 'react';
import toast from 'react-hot-toast';
import { isNativeApp } from './platform';

/**
 * Reliable device-location permission handling for the native shell.
 *
 * The WebView's own geolocation prompt (`onGeolocationPermissionsShowPrompt`)
 * caches its "denied" answer for the page session and stops re-prompting, and
 * a permanently-denied Android permission makes `ActivityCompat.requestPermissions`
 * a silent no-op. Driving the permission through the @capacitor/geolocation
 * plugin sidesteps both problems, and `AppSettings.openAppSettings` gives the
 * user a way back once Android refuses to show the dialog again.
 *
 * Every call below that crosses into native code (AppSettingsPlugin or
 * @capacitor/geolocation) is wrapped in a hard timeout. A missed native
 * callback — for any reason, on either side of the bridge — must never hang
 * this chain forever: that's exactly what showed up on-device as "Getting
 * location..." never resolving on cold start, and the recenter button doing
 * nothing at all when permission was denied. No native call here is trusted
 * to always call back.
 */

class NativeCallTimeoutError extends Error {}

// Pure status reads (no UI shown) — should be near-instant; a real answer
// this slow means something's wrong, not that the user is still deciding.
const STATUS_CALL_TIMEOUT_MS = 4000;
// Calls that may show a system dialog and wait on a human tapping it — give
// a real dialog plenty of time to be answered before we give up on it.
const INTERACTIVE_CALL_TIMEOUT_MS = 15000;

const withTimeout = (promise, ms) => Promise.race([
  Promise.resolve(promise),
  new Promise((_, reject) => {
    setTimeout(() => reject(new NativeCallTimeoutError('native_call_timeout')), ms);
  }),
]);

// A dynamic import() should be near-instant here (the module is already
// bundled locally, not fetched over a real network) — but it's still an async
// operation this code doesn't control, so it gets the same no-hang guarantee
// as everything else that crosses out of this file's direct control.
//
// IMPORTANT: a Capacitor plugin object (from registerPlugin(...)) answers a
// property-name lookup for *anything* — including "then" — with a callable
// that forwards to native code. Handing that object back as the fulfillment
// value of a promise (an async function's `return`, or a `.then()` callback's
// return value) makes the JS engine's own promise machinery treat it as a
// thenable and call `plugin.then(...)` to "unwrap" it — which Capacitor tries
// to run as a real native method named "then" and fails:
// `"X.then() is not implemented on android"`, thrown as an unhandled
// rejection that silently kills whatever awaited it. Confirmed on-device.
// The fix: the plugin object must never itself be a promise's resolution
// value. It's kept in a plain (non-promise) box; callers `await` a loader
// that resolves to nothing (undefined), THEN read the box with ordinary,
// non-async property access — never `await`ing anything that could itself
// resolve to the plugin object, even indirectly through another async
// function's `return`.
let geoPluginBox = { plugin: undefined }; // undefined = not loaded yet, null = load failed
let geoPluginLoading = null; // resolves to undefined only — safe to await anywhere
const ensureGeoPluginLoaded = () => {
  if (geoPluginBox.plugin !== undefined) return Promise.resolve();
  if (!geoPluginLoading) {
    geoPluginLoading = withTimeout(import('@capacitor/geolocation'), STATUS_CALL_TIMEOUT_MS)
      .then((m) => { geoPluginBox.plugin = m.Geolocation || null; })
      .catch(() => { geoPluginBox.plugin = null; });
  }
  return geoPluginLoading;
};
const readGeoPlugin = () => geoPluginBox.plugin || null;

let appSettingsPluginBox = { plugin: undefined };
let appSettingsPluginLoading = null;
const ensureAppSettingsPluginLoaded = () => {
  if (appSettingsPluginBox.plugin !== undefined) return Promise.resolve();
  if (!appSettingsPluginLoading) {
    appSettingsPluginLoading = withTimeout(import('@capacitor/core'), STATUS_CALL_TIMEOUT_MS)
      .then(({ registerPlugin }) => { appSettingsPluginBox.plugin = registerPlugin('AppSettings') || null; })
      .catch(() => { appSettingsPluginBox.plugin = null; });
  }
  return appSettingsPluginLoading;
};
const readAppSettingsPlugin = () => appSettingsPluginBox.plugin || null;

/** Open this app's OS settings screen so the user can grant location manually. */
export const openAppSettings = async () => {
  try {
    await ensureAppSettingsPluginLoaded();
    const plugin = readAppSettingsPlugin();
    await plugin?.openAppSettings?.();
  } catch {
    /* nothing else we can do from the web layer */
  }
};

const normalizeState = (status) => {
  if (status?.location === 'granted' || status?.coarseLocation === 'granted') {
    return 'granted';
  }
  if (
    status?.location === 'denied' &&
    (status?.coarseLocation === 'denied' || !status?.coarseLocation)
  ) {
    return 'denied';
  }
  return 'prompt'; // 'prompt' | 'prompt-with-rationale'
};

/**
 * Query native permission & GPS hardware status directly from AppSettingsPlugin.
 */
export const checkNativeLocationStatus = async () => {
  if (!isNativeApp()) return { permission: 'granted', locationServicesEnabled: true };
  await ensureAppSettingsPluginLoaded();
  const plugin = readAppSettingsPlugin();
  if (plugin?.checkLocationStatus) {
    try {
      const res = await withTimeout(plugin.checkLocationStatus(), STATUS_CALL_TIMEOUT_MS);
      return {
        permission: res?.permission || 'prompt',
        locationServicesEnabled: !!res?.locationServicesEnabled,
      };
    } catch {
      /* timeout or failure — fallthrough to @capacitor/geolocation */
    }
  }
  // Fallback to @capacitor/geolocation
  await ensureGeoPluginLoaded();
  const Geolocation = readGeoPlugin();
  if (!Geolocation) return { permission: 'granted', locationServicesEnabled: true };
  try {
    const state = normalizeState(await withTimeout(Geolocation.checkPermissions(), STATUS_CALL_TIMEOUT_MS));
    return { permission: state, locationServicesEnabled: true };
  } catch (e) {
    const msg = String(e?.message || '').toLowerCase();
    const isLocOff = msg.includes('location services are not enabled') || msg.includes('disabled');
    return { permission: 'prompt', locationServicesEnabled: !isLocOff };
  }
};

/**
 * Prompt Google Play Services 1-tap dialog: "To continue, turn on device location [OK]"
 */
export const promptEnableDeviceLocation = async () => {
  if (!isNativeApp()) return true;
  await ensureAppSettingsPluginLoaded();
  const plugin = readAppSettingsPlugin();
  if (!plugin?.promptEnableLocation) return true;
  try {
    const res = await withTimeout(plugin.promptEnableLocation(), INTERACTIVE_CALL_TIMEOUT_MS);
    return !!res?.enabled;
  } catch {
    // Timeout or failure — we don't actually know whether GPS is on. Assume
    // yes and let the caller proceed to a real GPS read, which will fail
    // cleanly on its own (a normal "unavailable" error) if it's genuinely
    // still off, rather than getting stuck here a second time.
    return true;
  }
};

/**
 * Natively request fine & coarse location permissions.
 */
export const requestNativeLocationPermission = async () => {
  if (!isNativeApp()) return 'granted';
  await ensureAppSettingsPluginLoaded();
  const plugin = readAppSettingsPlugin();
  if (plugin?.requestLocationPermission) {
    try {
      const res = await withTimeout(plugin.requestLocationPermission(), INTERACTIVE_CALL_TIMEOUT_MS);
      if (res?.granted) return 'granted';
      return res?.permission || 'denied';
    } catch (e) {
      if (e instanceof NativeCallTimeoutError) {
        // The native call may still be waiting on a real system dialog the
        // user hasn't answered yet — firing a second, competing permission
        // request below would collide with it (Android only tracks one
        // requestPermissions() at a time). Stop here rather than risk that;
        // "denied" surfaces the Settings prompt, and a real grant still
        // takes effect the next time this runs (recenter again, or reopen).
        return 'denied';
      }
      /* a real failure, not a timeout — safe to try the other path */
    }
  }
  await ensureGeoPluginLoaded();
  const Geolocation = readGeoPlugin();
  if (Geolocation?.requestPermissions) {
    try {
      return normalizeState(await withTimeout(Geolocation.requestPermissions({
        permissions: ['location', 'coarseLocation'],
      }), INTERACTIVE_CALL_TIMEOUT_MS));
    } catch {
      return 'denied';
    }
  }
  return 'granted';
};

/**
 * Returns 'granted' | 'denied' (permanent) | 'prompt'.
 * If permission is granted but device GPS is OFF, prompts the Google Play Services 1-tap dialog.
 */
export const ensureLocationPermission = async ({ request = true } = {}) => {
  if (!isNativeApp()) return 'granted';

  const status = await checkNativeLocationStatus();
  let state = status.permission;

  if (state === 'granted' || !request) {
    if (state === 'granted' && request && !status.locationServicesEnabled) {
      const enabled = await promptEnableDeviceLocation();
      if (!enabled) {
        throw new Error('location_disabled');
      }
    }
    return state;
  }

  if (state === 'prompt') {
    state = await requestNativeLocationPermission();
    if (state === 'granted' && !status.locationServicesEnabled) {
      const enabled = await promptEnableDeviceLocation();
      if (!enabled) {
        throw new Error('location_disabled');
      }
    }
    return state;
  }

  return 'denied';
};

export const checkLocationPermission = () => ensureLocationPermission({ request: false });

const label = (t, key, fallback) => {
  try {
    const value = t?.(key);
    return value && value !== key ? value : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Show a location-denied message. On native it carries an "Open Settings"
 * action, because that is the only way back once Android stops prompting.
 */
export const showLocationDeniedToast = (t) => {
  const message = label(
    t,
    'book.geoDenied',
    'Location is turned off for MyTownly Rides. Turn it on in Settings to use your current location.',
  );

  if (!isNativeApp()) {
    toast.error(message, { id: 'geo-denied', duration: 6000 });
    return;
  }

  toast.error(
    React.createElement(
      'span',
      { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
      message,
      React.createElement(
        'button',
        {
          type: 'button',
          onClick: () => {
            toast.dismiss();
            openAppSettings();
          },
          style: {
            alignSelf: 'flex-start',
            padding: '6px 14px',
            borderRadius: 10,
            border: 'none',
            background: 'var(--green-dark, #007a4d)',
            color: '#fff',
            fontWeight: 700,
            fontSize: 13,
          },
        },
        label(t, 'book.openSettings', 'Open Settings'),
      ),
    ),
    // Fixed id — a repeat call (e.g. a retry that resolves 'denied' again)
    // replaces this toast in place instead of stacking a new one on top.
    { id: 'geo-denied', duration: 9000 },
  );
};

/**
 * Show a location-disabled message (device GPS hardware is off).
 * On native it carries a "Turn On Location" action that re-triggers the Google 1-tap dialog.
 */
export const showLocationDisabledToast = (t, onRetry) => {
  const message = label(
    t,
    'book.locationServicesOff',
    'Device location is turned off. Turn on location to find your position.',
  );

  if (!isNativeApp()) {
    toast.error(message, { id: 'geo-disabled', duration: 6000 });
    return;
  }

  toast.error(
    React.createElement(
      'span',
      { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
      message,
      React.createElement(
        'button',
        {
          type: 'button',
          onClick: async () => {
            toast.dismiss();
            const enabled = await promptEnableDeviceLocation();
            if (enabled && typeof onRetry === 'function') {
              onRetry();
            }
          },
          style: {
            alignSelf: 'flex-start',
            padding: '6px 14px',
            borderRadius: 10,
            border: 'none',
            background: 'var(--green-dark, #007a4d)',
            color: '#fff',
            fontWeight: 700,
            fontSize: 13,
          },
        },
        label(t, 'book.turnOnLocation', 'Turn On Location'),
      ),
    ),
    { id: 'geo-disabled', duration: 9000 },
  );
};
